import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Edit, Plus, Trash2, Copy, Check, FileText, ChevronDown, ChevronUp,
  Hash, Calendar, Search, Filter, X, BadgeCheck, XCircle, HelpCircle,
  ClipboardList
} from 'lucide-react';
import apiService from '../services/api';
import { Form as FormType, Question } from '../types/api';

interface FormCardProps {
  form: FormType;
  isExpanded: boolean;
  onToggle: () => void;
  onEdit: (form: FormType) => void;
  onDelete: (form: FormType) => void;
  onCopyId: (id: string) => void;
  copiedFormId: string | null;
}

const FormCard: React.FC<FormCardProps> = ({ 
  form, isExpanded, onToggle, onEdit, onDelete, onCopyId, copiedFormId
}) => {
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getFormIcon = () => {
    return form.is_OnBoarding ? 'from-emerald-500 to-teal-600' : 'from-blue-500 to-indigo-600';
  };

  return (
    <div className="user-card">
      {/* Card Header */}
      <div className="user-card-header" onClick={onToggle}>
        <div className="user-card-left">
          <div className={`user-avatar-large bg-gradient-to-br ${getFormIcon()}`}>
            <FileText size={24} />
          </div>
          <div className="user-card-info">
            <div className="user-card-name">{form.form_name}</div>
            <div className="user-card-meta">
              <span className="user-card-id">
                <Hash size={12} />
                {form._id?.substring(0, 8)}...
              </span>
              <span className="user-card-email">
                <HelpCircle size={12} />
                {form.questions?.length || 0} questions
              </span>
            </div>
          </div>
        </div>
        
        <div className="user-card-right">
          <div className="user-card-badges">
            <span className={`user-badge ${form.is_OnBoarding ? 'badge-onboarding' : 'badge-regular'}`}>
              {form.is_OnBoarding ? 'Onboarding' : 'Regular'}
            </span>
            <span className="user-badge badge-questions">
              {form.questions?.length || 0} Q
            </span>
          </div>
          <button className="expand-btn" aria-label="Toggle details">
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>
      </div>

      {/* Expanded Content */}
      <div className={`user-card-body ${isExpanded ? 'expanded' : ''}`}>
        <div className="user-details-grid">
          {/* Form Information */}
          <div className="detail-section">
            <h4 className="detail-section-title">
              <FileText size={16} />
              Form Details
            </h4>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">Form Name</span>
                <span className="detail-value">{form.form_name}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Form ID</span>
                <div className="detail-value font-mono text-xs flex items-center gap-2">
                  {form._id}
                  <button
                    onClick={(e) => { e.stopPropagation(); onCopyId(form._id!); }}
                    className="copy-btn-inline"
                    title="Copy Form ID"
                  >
                    {copiedFormId === form._id ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
              <div className="detail-item">
                <span className="detail-label">Type</span>
                <span className={`detail-value ${form.is_OnBoarding ? 'text-emerald-600' : 'text-blue-600'}`}>
                  {form.is_OnBoarding ? 'Onboarding Form' : 'Regular Form'}
                </span>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="detail-section">
            <h4 className="detail-section-title">
              <Calendar size={16} />
              Timeline
            </h4>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">Created At</span>
                <span className="detail-value">{formatDate(form.createdAt)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Last Updated</span>
                <span className="detail-value">{formatDate(form.updatedAt)}</span>
              </div>
            </div>
          </div>

          {/* Questions Preview */}
          <div className="detail-section" style={{ gridColumn: 'span 2' }}>
            <h4 className="detail-section-title">
              <HelpCircle size={16} />
              Questions ({form.questions?.length || 0})
            </h4>
            <div className="questions-list">
              {form.questions?.slice(0, 5).map((q: Question, idx: number) => (
                <div key={q._id || idx} className="question-item">
                  <span className="question-number">{idx + 1}</span>
                  <span className="question-text">{q.question || q.questionText}</span>
                  <span className={`question-type ${q.isRequired ? 'required' : ''}`}>
                    {q.answer_type}
                    {q.isRequired && ' *'}
                  </span>
                </div>
              ))}
              {(form.questions?.length || 0) > 5 && (
                <div className="question-more">
                  +{form.questions!.length - 5} more questions
                </div>
              )}
              {(!form.questions || form.questions.length === 0) && (
                <div className="question-empty">No questions added yet</div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="user-card-actions">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(form); }}
            className="action-btn action-edit"
            title="Edit Form"
          >
            <Edit size={16} />
            Edit Form
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onCopyId(form._id!); }}
            className="action-btn action-copy"
            title="Copy Form ID"
          >
            {copiedFormId === form._id ? <Check size={16} /> : <Copy size={16} />}
            {copiedFormId === form._id ? 'Copied!' : 'Copy ID'}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(form); }}
            className="action-btn action-delete"
            title="Delete Form"
          >
            <Trash2 size={16} />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

const Forms: React.FC = () => {
  const navigate = useNavigate();
  const [forms, setForms] = useState<FormType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [copiedFormId, setCopiedFormId] = useState<string | null>(null);
  const [expandedForms, setExpandedForms] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');

  useEffect(() => {
    fetchForms();
  }, []);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const fetchForms = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAllForms();
      setForms(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch forms');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (formId: string) => {
    setExpandedForms(prev => {
      const newSet = new Set(prev);
      if (newSet.has(formId)) {
        newSet.delete(formId);
      } else {
        newSet.add(formId);
      }
      return newSet;
    });
  };

  const expandAll = () => {
    setExpandedForms(new Set(filteredForms.map(f => f._id!)));
  };

  const collapseAll = () => {
    setExpandedForms(new Set());
  };

  const handleDeleteForm = async (form: FormType) => {
    if (window.confirm(`Are you sure you want to delete "${form.form_name}"?`)) {
      try {
        await apiService.deleteForm(form._id!);
        setSuccess(`Form "${form.form_name}" deleted successfully`);
        await fetchForms();
      } catch (err: any) {
        setError(err.message || 'Failed to delete form');
      }
    }
  };

  const handleCopyFormId = async (fullId: string) => {
    try {
      await navigator.clipboard.writeText(fullId);
      setCopiedFormId(fullId);
      setTimeout(() => setCopiedFormId(null), 2000);
    } catch (e) {
      console.error('Failed to copy Form ID', e);
    }
  };

  const handleEditForm = (form: FormType) => {
    navigate(`/forms/builder/${form._id}`);
  };

  // Filter forms
  const filteredForms = forms.filter(form => {
    const matchesSearch = searchQuery === '' || 
      form.form_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      form._id?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesType = filterType === 'all' ||
      (filterType === 'onboarding' && form.is_OnBoarding) ||
      (filterType === 'regular' && !form.is_OnBoarding);

    return matchesSearch && matchesType;
  });

  return (
    <div className="users-page">
      {/* Header */}
      <div className="users-header">
        <div>
          <h2 className="users-title">Forms Management</h2>
          <p className="users-subtitle">Manage all forms and questionnaires • {forms.length} total forms</p>
        </div>
        <button onClick={() => navigate('/forms/builder')} className="btn btn-primary">
          <Plus size={16} />
          Create New Form
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="message message-error">
          <XCircle size={18} />
          {error}
          <button onClick={() => setError(null)} className="message-close">
            <X size={16} />
          </button>
        </div>
      )}

      {success && (
        <div className="message message-success">
          <BadgeCheck size={18} />
          {success}
          <button onClick={() => setSuccess(null)} className="message-close">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Filters & Search */}
      <div className="users-toolbar">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search by form name or ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="search-clear">
              <X size={16} />
            </button>
          )}
        </div>

        <div className="filters-group">
          <div className="filter-item">
            <Filter size={16} />
            <select 
              value={filterType} 
              onChange={(e) => setFilterType(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Types</option>
              <option value="onboarding">Onboarding</option>
              <option value="regular">Regular</option>
            </select>
          </div>

          <div className="expand-controls">
            <button onClick={expandAll} className="expand-btn-small" title="Expand All">
              <ChevronDown size={16} /> Expand All
            </button>
            <button onClick={collapseAll} className="expand-btn-small" title="Collapse All">
              <ChevronUp size={16} /> Collapse All
            </button>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div className="results-count">
        Showing {filteredForms.length} of {forms.length} forms
      </div>

      {/* Form Cards */}
      <div className="users-list">
        {loading ? (
          <div className="loading-state">
            <div className="spinner-large"></div>
            <p>Loading forms...</p>
          </div>
        ) : filteredForms.length === 0 ? (
          <div className="empty-state">
            <ClipboardList size={48} className="text-gray-300" />
            <h3>No forms found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : (
          filteredForms.map(form => (
            <FormCard
              key={form._id}
              form={form}
              isExpanded={expandedForms.has(form._id!)}
              onToggle={() => toggleExpanded(form._id!)}
              onEdit={handleEditForm}
              onDelete={handleDeleteForm}
              onCopyId={handleCopyFormId}
              copiedFormId={copiedFormId}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default Forms;
